import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import type { CallAnalysis } from "../../../api/generated";
import { ScoreSummary } from "./ScoreSummary";

interface ScorePanelProps {
  open: boolean;
  onClose: () => void;
  analysis: CallAnalysis | null;
}

function insightColor(type: string): "success" | "warning" | "info" {
  if (type === "strength") return "success";
  if (type === "weakness") return "warning";
  return "info";
}

function insightIcon(type: string): string {
  if (type === "strength") return "+";
  if (type === "weakness") return "!";
  return "->";
}

function MetadataRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
      <Typography variant="body2" sx={{
        color: "text.secondary"
      }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

export function ScorePanel({ open, onClose, analysis }: ScorePanelProps) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 400 }, p: 3 } } }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6">Call Analysis</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      {!analysis ? (
        <Typography sx={{
          color: "text.secondary"
        }}>
          No scores available yet. Scores will appear after the analysis is
          complete.
        </Typography>
      ) : (
        <>
          {/* Call Metadata */}
          {(analysis.call_duration_minutes != null ||
            analysis.talk_time_percentage != null ||
            analysis.prospect_name ||
            analysis.call_outcome ||
            analysis.relationship_status) && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Call Details
              </Typography>
              <MetadataRow label="Prospect" value={analysis.prospect_name} />
              <MetadataRow label="Company" value={analysis.prospect_company} />
              <MetadataRow label="Outcome" value={analysis.call_outcome} />
              <MetadataRow
                label="Relationship"
                value={analysis.relationship_status}
              />
              <MetadataRow
                label="Duration"
                value={
                  analysis.call_duration_minutes != null
                    ? `${analysis.call_duration_minutes} min`
                    : null
                }
              />
              <MetadataRow
                label="Talk Time"
                value={
                  analysis.talk_time_percentage != null
                    ? `${analysis.talk_time_percentage}%`
                    : null
                }
              />
              <Divider sx={{ mt: 2 }} />
            </Box>
          )}

          {/* Scores */}
          <ScoreSummary analysis={analysis} />

          {/* Insights */}
          {analysis.insights && analysis.insights.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Insights
              </Typography>
              {analysis.insights.map((insight, idx) => (
                <Box
                  key={idx}
                  sx={{
                    mb: 2,
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                  }}
                >
                  <Chip
                    label={insightIcon(insight.type)}
                    color={insightColor(insight.type)}
                    size="small"
                    sx={{ minWidth: 32, fontWeight: 700 }}
                  />
                  <Box>
                    <Typography variant="body2">{insight.text}</Typography>
                    {insight.category && (
                      <Typography variant="caption" sx={{
                        color: "text.secondary"
                      }}>
                        {insight.category}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </>
          )}
        </>
      )}
    </Drawer>
  );
}
